import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { getRoles, deleteRole } from "../../../Api/RbacAdminApi";
import CODE from "../../../Static/Constants/StatusCodes";
import { useToast } from "../../../Hooks/useToast";
import CreateEditRoleDialog from "./CreateEditRoleDialog";

const RoleList = ({ refreshKey, selectedRole, onSelectRole, onRolesChanged }) => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editRole, setEditRole] = useState(null);
  const [deleteConfirmRole, setDeleteConfirmRole] = useState(null);
  const { openNotification } = useToast();

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const res = await getRoles();
      if (res?.responseStatus?.code === CODE.SUCCESS) {
        setRoles(res.response?.data || []);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, [refreshKey]);

  const handleDeleteConfirmed = async () => {
    if (!deleteConfirmRole) return;
    const res = await deleteRole(deleteConfirmRole.id);
    if (res?.responseStatus?.code === CODE.SUCCESS) {
      openNotification({ text: `Role "${deleteConfirmRole.roleName}" deleted` });
      if (selectedRole?.id === deleteConfirmRole.id) onSelectRole(null);
      fetchRoles();
      onRolesChanged();
    }
    setDeleteConfirmRole(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <>
      <List
        sx={{
          overflowY: "auto",
          height: "100%",
          p: 0,
        }}
      >
        {roles.map((role) => (
          <ListItemButton
            key={role.id}
            selected={selectedRole?.id === role.id}
            onClick={() => onSelectRole(role)}
            sx={{
              borderBottom: "1px solid #F0F0F0",
              borderLeft: selectedRole?.id === role.id
                ? "3px solid #4D44D8"
                : "3px solid transparent",
              pr: 10,
              "&.Mui-selected": {
                backgroundColor: "#F2F3FB",
                "&:hover": { backgroundColor: "#EEEFFF" },
              },
              "&:hover": { backgroundColor: "#F8F8FF" },
            }}
          >
            <ListItemText
              primary={
                <Typography
                  sx={{
                    fontSize: 15,
                    fontWeight: selectedRole?.id === role.id ? 700 : 600,
                    color: selectedRole?.id === role.id ? "#4D44D8" : "#2E2E2E",
                  }}
                >
                  {role.roleName}
                </Typography>
              }
              secondary={
                <Box component="span">
                  <Typography
                    color="text.secondary"
                    display="block"
                    sx={{ fontSize: 13 }}
                  >
                    {role.roleDescription || "—"}
                  </Typography>
                  <Typography
                    color="text.secondary"
                    sx={{ fontSize: 13 }}
                  >
                    {role.userCount ?? 0} users
                  </Typography>
                </Box>
              }
            />
            <ListItemSecondaryAction>
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditRole(role);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteConfirmRole(role);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </ListItemSecondaryAction>
          </ListItemButton>
        ))}
        {roles.length === 0 && (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No roles found
            </Typography>
          </Box>
        )}
      </List>

      {editRole && (
        <CreateEditRoleDialog
          open={!!editRole}
          mode="edit"
          role={editRole}
          onClose={() => setEditRole(null)}
          onSuccess={() => {
            setEditRole(null);
            fetchRoles();
            onRolesChanged();
          }}
        />
      )}

      <Dialog open={!!deleteConfirmRole} onClose={() => setDeleteConfirmRole(null)}>
        <DialogTitle>Delete Role</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete &quot;{deleteConfirmRole?.roleName}&quot;?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmRole(null)}>Cancel</Button>
          <Button color="error" onClick={handleDeleteConfirmed}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RoleList;
